/**
 * fetchCache
 * hasInFetchCache
 * normalizeSectionId
 * fetchConfig
 * debounce
 * throttle
 * formatMoney
 * $4 $$4 $id4
 * matchMediaQuery
 * onMediaQueryChange
 * createImg
 * renderSrcset
 * requestIdleCallback
 * waitForEvent
 */

// In-memory cache Map
const genericCache = new Map();

/**
 * Fetch with in-memory cache and custom options.
 * @param {string} url - The URL to fetch.
 * @param {string} prefix
 * @param {object} [options={}] - Fetch options (method, headers, body, etc).
 * @returns {Promise<Response>} - Cached or fetched Response.
 */
export async function fetchCache(url, prefix = '', options = {}) {
  // console.log(genericCache)
  // Use JSON.stringify of both URL and options to uniquely cache
  const cacheKey = url + prefix;
  if (genericCache.has(cacheKey)) {
    const { body, headers } = genericCache.get(cacheKey);
    return Promise.resolve(new Response(body, { headers }));
  }

  const res = await fetch(url, options);
  if (res.status === 200) {
    const clone = res.clone();

    const body = await clone.text(); // safe for most content types
    const headersObj = {};
    for (const [key, value] of clone.headers.entries()) {
      headersObj[key] = value;
    }
    if(!url.includes('quick_order_list')){
      genericCache.set(cacheKey, { body, headers: headersObj });
    }
  }
  return res;
}

export function hasInFetchCache(urlPrefix) {
  return genericCache.has(urlPrefix)
}

// const SECTION_ID_PREFIX = 'shopify-section-';
/**
 * Builds a section selector
 * @param {string} sectionId - The section ID
 * @returns {string} The section selector
 */
// export function buildSectionSelector(sectionId) {
//   return `${SECTION_ID_PREFIX}${sectionId}`;
// }

/**
 * Normalizes a section ID
 * @param {string} sectionId - The section ID
 * @returns {string} The normalized section ID
 */
// export function normalizeSectionId(sectionId) {
//   return sectionId.replace(new RegExp(`^${SECTION_ID_PREFIX}`), '');
// }

/**
 * Creates a fetch configuration object
 * @param {string} [type] The type of response to expect
 * @param {Object} [config] The config of the request
 * @param {FetchConfig['body']} [config.body] The body of the request
 * @param {FetchConfig['headers']} [config.headers] The headers of the request
 * @returns {RequestInit} The fetch configuration object
 */
export function fetchConfig(type = 'json', config = {}) {
  /** @type {Headers} */
  const headers = { 'Content-Type': 'application/json', Accept: `application/${type}`, ...config.headers };

  if (type === 'javascript') {
    headers['X-Requested-With'] = 'XMLHttpRequest';
    delete headers['Content-Type'];
  }

  return {
    method: 'POST',
    headers: /** @type {HeadersInit} */ (headers),
    body: config.body,
  };
}

/**
 * Creates a debounced function that delays calling the provided function (fn)
 * until after wait milliseconds have elapsed since the last time
 * the debounced function was invoked. The returned function has a .cancel()
 * method to cancel any pending calls.
 *
 * @template {(...args: any[]) => any} T
 * @param {T} fn The function to debounce
 * @param {number} wait The time (in milliseconds) to wait before calling fn
 * @returns {T & { cancel(): void }} A debounced version of fn with a .cancel() method
 */
export function debounce(fn, wait) {
  /** @type {number | undefined} */
  let timeout;

  /** @param {...any} args */
  function debounced(...args) {
    clearTimeout(timeout);
    timeout = setTimeout(() => fn.apply(this, args), wait);
  }

  // Add the .cancel method:
  debounced.cancel = () => {
    clearTimeout(timeout);
  };

  return /** @type {T & { cancel(): void }} */ (debounced);
}

/**
 * Creates a throttled function that calls the provided function (fn) at most once per every wait milliseconds
 *
 * @template {(...args: any[]) => any} T
 * @param {T} fn The function to throttle
 * @param {number} delay The time (in milliseconds) to wait before calling fn
 * @returns {T & { cancel(): void }} A throttled version of fn with a .cancel() method
 */
export function throttle(callback) {
  let requestId = null;
  let lastArgs = null;

  const throttled = function (...args) {
    lastArgs = args;
    if (requestId === null) {
      requestId = requestAnimationFrame(() => {
        callback.apply(this, lastArgs);
        requestId = null;
        lastArgs = null;
      });
    }
  };

  throttled.cancel = () => {
    if (requestId !== null) {
      cancelAnimationFrame(requestId);
      requestId = null;
    }
  };

  return throttled;
}
// export function throttleWithDelay(fn, delay) {
//   let lastCall = 0;

//   /** @param {...any} args */
//   function throttled(...args) {
//     const now = performance.now();
//     // If the time since the last call exceeds the delay, execute the callback
//     if (now - lastCall >= delay) {
//       lastCall = now;
//       fn.apply(this, args);
//     }
//   }

//   throttled.cancel = () => {
//     lastCall = performance.now();
//   };

//   return /** @type {T & { cancel(): void }} */ (throttled);
// }

/**
 * Format a money value
 * @param {string} cents The cents to format
 * @returns {string} The formatted value
 */

const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
function defaultTo(value2, defaultValue) {
  return value2 == null || value2 !== value2 ? defaultValue : value2;
}
function formatWithDelimiters(number, precision, thousands, decimal, scale) {
  precision = defaultTo(precision, 2);
  thousands = defaultTo(thousands, ",");
  decimal = defaultTo(decimal, ".");
  scale = defaultTo(scale, 100);
  if (isNaN(number) || number == null) {
    return 0;
  }
  number = (number / scale).toFixed(precision);
  let parts = number.split("."), dollarsAmount = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, "$1" + thousands), centsAmount = parts[1] ? decimal + parts[1] : "";
  return dollarsAmount + centsAmount;
}
// AYUM: subunit scale and decimal count of the shop currency, read from the
// server's own `1 | money` output (themeHDN.settings.ayumMoneyProbe, set in
// snippets/js-variables.liquid). Without it the client assumes 100 / 2 dp,
// which disagrees with Liquid for 3-decimal currencies such as KWD.
// Only applied to the shop's own formats; any other format keeps 100 / 2.
let ayumMoneyCal;
function ayumMoneyCalibration(formatString) {
  const s = window.themeHDN?.settings;
  if (!s || (formatString !== s.moneyFormat && formatString !== s.moneyWithCurrencyFormat)) return null;
  if (ayumMoneyCal !== undefined) return ayumMoneyCal;
  ayumMoneyCal = null;
  const probe = s.ayumMoneyProbe;
  const fmtParts = typeof s.moneyFormat === "string" ? s.moneyFormat.split(placeholderRegex) : [];
  if (typeof probe !== "string" || fmtParts.length !== 3) return ayumMoneyCal;
  const [head, name, tail] = fmtParts;
  if (name.indexOf("no_decimals") !== -1 || !probe.startsWith(head) || !probe.endsWith(tail)) return ayumMoneyCal;
  const amount = probe.slice(head.length, probe.length - tail.length);
  const sep = name.indexOf("comma_separator") !== -1 || name === "amount_with_space_separator" ? "," : ".";
  const frac = amount.slice(amount.lastIndexOf(sep) + 1);
  const one = frac.indexOf("1");
  if (amount.indexOf(sep) === -1 || !/^\d+$/.test(frac) || one === -1) return ayumMoneyCal;
  ayumMoneyCal = { scale: Math.pow(10, one + 1), precision: frac.length };
  return ayumMoneyCal;
}
// Subunits per whole unit for a format (100 unless the shop currency says otherwise).
export function ayumMoneyScale(format = "") {
  const cal = ayumMoneyCalibration(format || window.themeHDN?.settings?.moneyFormat);
  return cal ? cal.scale : 100;
}

export function formatMoney(cents, format = "") {
  const formatString = format || window.themeHDN.settings.moneyFormat;
  const cal = ayumMoneyCalibration(formatString);
  if (typeof cents === "string") {
    // AYUM: a decimal string ("5.000") is whole units — convert with the real
    // scale instead of deleting the dot (which assumed exactly 2 decimals).
    cents = cents.indexOf(".") !== -1 ? Math.round(parseFloat(cents) * (cal ? cal.scale : 100)) : cents;
  }
  const dp = cal ? cal.precision : 2;
  const scale = cal ? cal.scale : 100;
  let value = "";
  switch (formatString.match(placeholderRegex)[1]) {
    case "amount":
      value = formatWithDelimiters(cents, dp, ",", ".", scale);
      break;
    case "amount_no_decimals":
      value = formatWithDelimiters(cents, 0);
      break;
    case "amount_with_space_separator":
      value = formatWithDelimiters(cents, dp, " ", ".", scale);
      break;
    case "amount_with_comma_separator":
      value = formatWithDelimiters(cents, dp, ".", ",", scale);
      break;
    case "amount_with_apostrophe_separator":
      value = formatWithDelimiters(cents, dp, "'", ".", scale);
      break;
    case "amount_no_decimals_with_comma_separator":
      value = formatWithDelimiters(cents, 0, ".", ",");
      break;
    case "amount_no_decimals_with_space_separator":
      value = formatWithDelimiters(cents, 0, " ");
      break;
    case "amount_no_decimals_with_apostrophe_separator":
      value = formatWithDelimiters(cents, 0, "'");
      break;
  }
  if (formatString.indexOf("with_comma_separator") !== -1) {
    return formatString.replace(placeholderRegex, value);
  } else {
    return formatString.replace(placeholderRegex, value);
  }
}

// Selector
export function $4(selector, container = document) {
	return container.querySelector(selector);
};
export function $id4(selector) {
  return document.getElementById(selector);
}
export function $$4(selector, container = document) {
  return [...container.querySelectorAll(selector)];
}

/**
 * Map of predefined media queries
 */
const predefinedMediaQueries = {
  mobile: 'screen and (max-width: 767px)',
  tablet: 'screen and (min-width: 768px) and (max-width: 1149px)',
  hover: 'screen and (hover: hover) and (pointer: fine)',
  motion: '(prefers-reduced-motion: no-preference)',
  //motionReduce: '(prefers-reduced-motion: reduce)',
};
/**
 * Check whether a media query matches the current environment
 *
 * @param {string} type - Either a predefined key (e.g. "mobile") or a raw media query string.
 * @returns {boolean} - true if it matches, false otherwise
 */
export function matchMediaQuery(type) {
  return window.matchMedia(predefinedMediaQueries[type] || type).matches;
}
/**
 * Add a listener to media query changes
 *
 * @param {string} type - Predefined key or raw media query string
 * @param {(event: MediaQueryListEvent) => void} callback - Handler function
 * @returns {MediaQueryList} - The MediaQueryList object so you can manage/remove later if needed
 */
// export function onMediaQueryChange(type, callback) {
//   const mql = window.matchMedia(predefinedMediaQueries[type] || type);
//   mql.addEventListener('change', callback);
//   return mql;
// }
// onMediaQueryChange('mobile', (e) => {
//   console.log('Mobile media query changed:', e.matches);
// });

/**
 * Create an <img> element with given width and height
 * @param {Object} imgObj
 * @returns {HTMLImageElement}
 */
export function createImg(imgObj = {}) {
  const img = new Image(),
  attrs = imgObj.attrs || {},
  {alt, src} = imgObj;
  for (const key in attrs) {
    img.setAttribute(key, attrs[key]);
  }
  img.alt = alt || "";
  img.src = src;
  img.srcset = renderSrcset(src, JSON.parse(attrs['data-widths']));

  if (imgObj.onload) {
    img.addEventListener("load", function() {
      const parentImage = img.parentElement || img;
      parentImage.removeAttribute('loading');
      parentImage.setAttribute('loaded', '');
    }, {once : true});
  }
  return img;
}
/**
 * Ensure the image src starts with 'https://'
 * @param {string} src - Original image URL
 * @returns {string} Normalized image URL
 */
function normalizeImageSrc(src) {
  if (/^https?:\/\//.test(src)) {
    return src;
  }
  return `https://${src.replace(/^\/+/, '')}`;
}
export function renderSrcset(baseSrc, widths) {
  const url = new URL(normalizeImageSrc(baseSrc)),
      maxWidth = parseInt( url.searchParams.get("width") || widths[widths.length - 1]);
  return widths
    .filter((w) => w <= maxWidth)
    .map((w) => {
      url.searchParams.set('width', w);
      return `${url.toString()} ${w}w`;
    })
    .join(', ');
}

/**
 * Request an idle callback with timeout guarantee
 * Ensures callback is always executed even if browser never becomes idle
 * @param {Function} callback - The function to execute
 * @param {Object} [options] - Options object (optional, for compatibility)
 * @param {number} [options.timeout=2000] - Maximum time to wait before forcing execution (ms)
 * @returns {number} Request ID
 */
export function requestIdleCallback(callback, options = {}) {
  const timeout = options?.timeout || 2000;
  
  // Browser supports requestIdleCallback - use it with timeout fallback
  let timeoutId;
  let isExecuted = false;
  
  const wrappedCallback = (deadline) => {
    if (isExecuted) return;
    isExecuted = true;
    clearTimeout(timeoutId);
    // Call original callback, ignore deadline if not needed
    if (callback.length === 0) {
      callback();
    } else {
      callback(deadline);
    }
  };
  
  // Set timeout fallback to ensure callback always executes
  // This prevents the callback from never being called if browser is always busy
  timeoutId = setTimeout(() => {
    if (!isExecuted) {
      isExecuted = true;
      // Create a mock deadline object for consistency
      const mockDeadline = { 
        timeRemaining: () => 0, 
        didTimeout: true 
      };
      if (callback.length === 0) {
        callback();
      } else {
        callback(mockDeadline);
      }
    }
  }, timeout);
  
  return window.requestIdleCallback(wrappedCallback, { timeout });
}

/**
 * Cancel an idle callback request
 * @param {number} id - The ID returned by requestIdleCallback
 */
export function cancelIdleCallback(id) {
  window.cancelIdleCallback(id);
}

/**
 * Wait for a specific event on a DOM element, then resolve with the event object.
 *
 * @param {string|Element} target - CSS selector or DOM element
 * @param {string} eventName - Event name (e.g. 'click', 'mouseover', 'submit')
 * @returns {Promise<Event>} Resolves when the event is triggered
 */
// export function waitForEvent(target, eventName) {
//   return new Promise((resolve, reject) => {
//     const el = typeof target === 'string' ? document.querySelector(target) : target;
//     if (!el) {
//       reject(new Error(`Element not found: ${target}`));
//       return;
//     }

//     const handler = (event) => {
//       el.removeEventListener(eventName, handler); // Auto-remove after 1 trigger
//       resolve(event);
//     };

//     el.addEventListener(eventName, handler);
//   });
// }